import { ValuationMethod } from '@popety-io/types'

// Method colors - Single source of truth
export const VALUATION_METHOD_COLORS: Record<ValuationMethod, string> = {
  [ValuationMethod.COST_BASED_INTRINSIC]: '#FF6B6B', // Red
  [ValuationMethod.COST_BASED_DEVELOPER]: '#FFE66D', // Yellow
  [ValuationMethod.INCOME_BASED_STATIC]: '#4ECDC4', // Teal
  [ValuationMethod.INCOME_BASED_DYNAMIC]: '#95E1D3', // Light teal
  [ValuationMethod.COMPARABLE_SALES]: '#C7CEEA', // Light purple
  [ValuationMethod.CUSTOM]: '#F38181', // Coral
  [ValuationMethod.AVM]: '#00ADE6', // Cyan (same as Popety.io)
}

/**
 * Get the color for a valuation method
 * Prioritizes custom color if provided, falls back to method-based color
 */
export const getMethodColor = (
  method: ValuationMethod,
  customColor?: string
): string => {
  if (customColor) {
    return customColor
  }
  return VALUATION_METHOD_COLORS[method] || '#888888'
}

/**
 * Get the i18n key for a valuation method display name
 */
export const getMethodI18nKey = (method: ValuationMethod): string => {
  switch (method) {
    case ValuationMethod.COST_BASED_INTRINSIC:
      return 'manualValuations.methods.intrinsic'
    case ValuationMethod.COST_BASED_DEVELOPER:
      return 'manualValuations.methods.developer'
    case ValuationMethod.INCOME_BASED_STATIC:
      return 'manualValuations.methods.staticYield'
    case ValuationMethod.INCOME_BASED_DYNAMIC:
      return 'manualValuations.methods.dcf'
    case ValuationMethod.COMPARABLE_SALES:
      return 'manualValuations.methods.comparable'
    case ValuationMethod.CUSTOM:
      return 'manualValuations.methods.custom'
    case ValuationMethod.AVM:
      return 'manualValuations.methods.avm'
    default:
      return 'manualValuations.methods.custom'
  }
}

/**
 * Get the fallback display name for a valuation method (used when i18n not available)
 */
export const getMethodDisplayName = (method: ValuationMethod): string => {
  switch (method) {
    case ValuationMethod.COST_BASED_INTRINSIC:
      return 'Intrinsic Value Method'
    case ValuationMethod.COST_BASED_DEVELOPER:
      return 'Developer Method'
    case ValuationMethod.INCOME_BASED_STATIC:
      return 'Static Yield Method'
    case ValuationMethod.INCOME_BASED_DYNAMIC:
      return 'DCF Method'
    case ValuationMethod.COMPARABLE_SALES:
      return 'Comparable Sales'
    case ValuationMethod.CUSTOM:
      return 'Custom Valuation'
    case ValuationMethod.AVM:
      return 'Automated Valuation'
    default:
      return 'Custom Method'
  }
}

/**
 * Get the badge variant for a valuation method
 */
export const getMethodBadgeVariant = (method: ValuationMethod): string => {
  switch (method) {
    case ValuationMethod.COST_BASED_INTRINSIC:
    case ValuationMethod.COST_BASED_DEVELOPER:
      return 'cost'
    case ValuationMethod.INCOME_BASED_STATIC:
    case ValuationMethod.INCOME_BASED_DYNAMIC:
      return 'income'
    case ValuationMethod.COMPARABLE_SALES:
      return 'comparable'
    case ValuationMethod.CUSTOM:
      return 'manual'
    case ValuationMethod.AVM:
      return 'avm'
    default:
      return 'default'
  }
}

/**
 * Get the i18n key for a confidence level
 */
export const getConfidenceI18nKey = (confidence?: number): string => {
  if (!confidence || confidence === 2)
    return 'manualValuations.confidence.medium'
  if (confidence === 1) return 'manualValuations.confidence.low'
  if (confidence >= 3) return 'manualValuations.confidence.high'
  return 'manualValuations.confidence.medium'
}

/**
 * Get the fallback label for a confidence level (used when i18n not available)
 */
export const getConfidenceLabel = (confidence?: number): string => {
  if (!confidence || confidence === 2) return 'Medium'
  if (confidence === 1) return 'Low'
  if (confidence >= 3) return 'High'
  return 'Medium'
}
